import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { tContent } from "@/lib/content-i18n";
import { useClock } from "@/lib/clock";

const SurpriseNotifCtx = createContext(null);
const SEEN_KEY = "wedding_seen_unlocked_v1";
const PERM_KEY = "wedding_notif_perm_asked";

function readSeen() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); }
  catch { return new Set(); }
}
function writeSeen(set) {
  localStorage.setItem(SEEN_KEY, JSON.stringify([...set]));
}

// Generate a soft chime via Web Audio API
function playChime() {
  try {
    // eslint-disable-next-line no-undef
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [
      { f: 880, t: now + 0.0, d: 0.25 },
      { f: 1175, t: now + 0.18, d: 0.3 },
      { f: 1568, t: now + 0.36, d: 0.45 },
    ].forEach(({ f, t, d }) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(f, t);
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.18, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + d);
      o.connect(g).connect(ctx.destination);
      o.start(t); o.stop(t + d + 0.05);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1500);
  } catch { /* noop */ }
}

// Wedding palette confetti — full-screen burst like a real cannon
function burstConfetti() {
  const colors = ["#7B3643", "#C8A55E", "#F6F3ED", "#6B7063", "#A78A52", "#E5D9D8"];

  // 1) Initial heavy cannon blast from both bottom corners
  const cannonBase = {
    spread: 90,
    ticks: 220,
    gravity: 0.85,
    decay: 0.92,
    startVelocity: 75,
    scalar: 1.2,
    colors,
  };
  confetti({ ...cannonBase, particleCount: 220, angle: 55, origin: { x: 0, y: 0.85 } });
  confetti({ ...cannonBase, particleCount: 220, angle: 125, origin: { x: 1, y: 0.85 } });

  // 2) Big top-center burst that rains down
  setTimeout(() => {
    confetti({
      particleCount: 250,
      spread: 160,
      startVelocity: 45,
      ticks: 300,
      gravity: 0.7,
      decay: 0.93,
      scalar: 1.1,
      origin: { x: 0.5, y: 0.2 },
      colors,
    });
  }, 250);

  // 3) Continuous falling rain for ~2.5s
  const end = Date.now() + 2200;
  (function frame() {
    confetti({
      particleCount: 5,
      angle: 60,
      spread: 55,
      startVelocity: 40,
      origin: { x: 0, y: 0.9 },
      colors,
    });
    confetti({
      particleCount: 5,
      angle: 120,
      spread: 55,
      startVelocity: 40,
      origin: { x: 1, y: 0.9 },
      colors,
    });
    confetti({
      particleCount: 3,
      angle: 270,
      spread: 90,
      startVelocity: 20,
      gravity: 0.6,
      scalar: 0.9,
      origin: { x: Math.random(), y: 0 },
      colors,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

export const SurpriseNotifProvider = ({ children }) => {
  const { lang, t } = useI18n();
  const { demoOn, now: clockNow } = useClock();
  const [flash, setFlash] = useState(null); // { title } | null
  const seenRef = useRef(readSeen());
  const initializedRef = useRef(false);

  const fireNotification = useCallback((s) => {
    const title = tContent(s.title, lang, "surprise");
    const description = tContent(s.description, lang, "surprise");
    const unlockedText = lang === "ca" ? "Sorpresa desbloquejada!" : "¡Sorpresa desbloqueada!";
    // Toast
    toast.success(`${unlockedText} ${title}`, { duration: 6000 });
    // Sound
    playChime();
    // Confetti
    burstConfetti();
    // Flash banner
    setFlash({ title, description });
    setTimeout(() => setFlash(null), 5500);
    // Browser push notification
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      try {
        new Notification(lang === "ca" ? "Nova sorpresa desbloquejada!" : "¡Sorpresa nueva desbloqueada!", {
          body: title,
          icon: "/favicon.ico",
          tag: `surprise-${s.id}`,
        });
      } catch { /* noop */ }
    }
  }, [lang, t]);

  const poll = useCallback(async () => {
    try {
      // When demo mode is active, pass demo_time so backend computes unlocks against it
      const params = demoOn ? { demo_time: clockNow.toISOString() } : {};
      const { data } = await api.get("/surprises", { params });
      const unlocked = (data || []).filter((s) => s.unlocked);
      const unlockedIds = unlocked.map((s) => s.id);

      // Demo transitions are not "real" unlocks — track them in a separate set so
      // turning demo on/off doesn't fire stale notifications.
      const stateKey = demoOn ? "demo" : "real";
      if (!initializedRef.current || seenRef.current._key !== stateKey) {
        seenRef.current = new Set(unlockedIds);
        seenRef.current._key = stateKey;
        if (!demoOn) writeSeen(seenRef.current);
        initializedRef.current = true;
        return;
      }

      const newOnes = unlocked.filter((s) => !seenRef.current.has(s.id));
      if (newOnes.length > 0) {
        for (const s of newOnes) {
          fireNotification(s);
          seenRef.current.add(s.id);
        }
        if (!demoOn) writeSeen(seenRef.current);
      }
    } catch { /* network blip */ }
  }, [fireNotification, demoOn, clockNow]);

  useEffect(() => {
    poll(); // initial
    // Faster poll while in demo so unlocks happen near instantly when you jump times
    const id = setInterval(poll, demoOn ? 4000 : 20000);
    return () => clearInterval(id);
  }, [poll, demoOn]);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") {
      toast.error("Tu navegador no admite notificaciones");
      return "unsupported";
    }
    if (Notification.permission === "granted") return "granted";
    try {
      const p = await Notification.requestPermission();
      localStorage.setItem(PERM_KEY, p);
      if (p === "granted") {
        toast.success("Notificaciones activadas ✿");
      } else {
        toast("Notificaciones rechazadas. Aun así te avisaremos al abrir la web.");
      }
      return p;
    } catch {
      return "default";
    }
  }, []);

  const triggerDemo = useCallback(() => {
    fireNotification({
      id: "demo",
      title: "Ventiladores",
      description: "Para refrescarse durante la ceremonia al aire libre.",
    });
  }, [fireNotification]);

  return (
    <SurpriseNotifCtx.Provider value={{ requestPermission, triggerDemo }}>
      {children}
      {flash && (
        <div className="fixed top-4 inset-x-0 z-[90] flex justify-center px-4 pointer-events-none" data-testid="surprise-flash">
          <div className="pointer-events-auto max-w-md w-full rounded-2xl bg-[color:var(--ink-wine)] text-white p-4 shadow-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top">
            <span className="text-2xl" role="img" aria-label="sparkles">✦</span>
            <div className="flex-1 min-w-0">
              <p className="text-[0.65rem] tracking-[0.3em] uppercase font-bold opacity-80">¡Sorpresa desbloqueada!</p>
              <p className="font-script-alt text-2xl leading-tight">{flash.title}</p>
            </div>
          </div>
        </div>
      )}
    </SurpriseNotifCtx.Provider>
  );
};

export const useSurpriseNotif = () => useContext(SurpriseNotifCtx);
